import { useEffect, useState } from "react";
import axios from "axios";
import { X, Mail, Lock, User, Loader2, Sparkles, Fingerprint } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isPasskeySupported, isPlatformAuthenticatorAvailable } from "@/lib/passkeys";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AuthModal({ open, onClose, defaultMode = "signin" }) {
  const [mode, setMode] = useState(defaultMode); // "signin" | "signup" | "forgot"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const { login, loginWithPasskey, register } = useAuth();

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isPasskeySupported()) return;
      const ok = await isPlatformAuthenticatorAvailable();
      if (alive) setPasskeyReady(ok);
    })();
    return () => { alive = false; };
  }, []);

  if (!open) return null;

  const doPasskeyLogin = async () => {
    setPasskeyBusy(true);
    setErr("");
    try {
      await loginWithPasskey();
      onClose();
    } catch (e) {
      setErr(e?.message || "Face ID sign-in failed.");
    } finally {
      setPasskeyBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: email.toLowerCase().trim() });
        setForgotSent(true);
      } else if (mode === "signup") {
        await register(name.trim() || "Athlete", email.toLowerCase(), password, rememberMe);
        onClose();
      } else {
        await login(email.toLowerCase(), password, rememberMe);
        onClose();
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      data-testid="auth-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6 my-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="auth-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-500 hover:text-white"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
          {mode === "signup" ? "Join The Movement" : mode === "forgot" ? "Account Recovery" : "Welcome Back"}
        </div>
        <h3 className="font-display text-3xl text-white mb-1 leading-tight">
          {mode === "signup" ? "Create Account" : mode === "forgot" ? "Forgot Password" : "Sign In"}
        </h3>
        <p className="text-neutral-400 text-xs mb-5">
          {mode === "forgot"
            ? "We'll send a one-hour reset link to your email."
            : "🌈 Mind · Body · Soul"}
        </p>

        {/* Google + divider — only on sign-in / sign-up. Forgot password is email-only. */}
        {mode !== "forgot" && (
          <>
            {/* 🔐 Face ID / passkey — surfaced only when platform authenticator is available.
                Enabled on sign-in mode; on sign-up we still show it in case they already
                registered a passkey elsewhere. */}
            {passkeyReady && (
              <button
                data-testid="auth-passkey"
                type="button"
                onClick={doPasskeyLogin}
                disabled={passkeyBusy || busy}
                className="w-full bg-[#0A0A0A] text-white font-bold py-3 px-4 mb-3 flex items-center justify-center gap-3 border-2 border-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50 transition-colors"
              >
                {passkeyBusy ? (
                  <><Loader2 size={16} className="animate-spin" /> Waiting for your device…</>
                ) : (
                  <><Fingerprint size={18} className="text-[#D4AF37]" /> Sign in with Face ID / Passkey</>
                )}
              </button>
            )}

            <button
              data-testid="auth-google"
              onClick={startGoogle}
              className="w-full bg-white text-black font-bold py-3 px-4 mb-3 flex items-center justify-center gap-3 hover:bg-neutral-100 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-zinc-800" />
              <span className="text-[10px] uppercase tracking-widest text-neutral-600">
                or with email
              </span>
              <div className="flex-1 h-px bg-zinc-800" />
            </div>
          </>
        )}

        {/* Success state for forgot-password — replaces the form once the
            backend confirms the request was queued. Backend returns the same
            generic response whether the email exists or not (enum-attack safe). */}
        {mode === "forgot" && forgotSent && (
          <div data-testid="auth-forgot-success" className="border border-[#D4AF37]/60 bg-[#D4AF37]/10 p-4 mb-4 text-sm text-amber-100">
            <div className="font-bold text-[#D4AF37] mb-1">Check your email.</div>
            <p className="text-neutral-300 text-xs leading-relaxed">
              If <span className="text-white font-semibold">{email}</span> is registered, a reset link is on its way. It expires in <span className="text-white">1 hour</span>.
            </p>
            <button
              type="button"
              data-testid="auth-forgot-back-to-signin"
              onClick={() => { setMode("signin"); setForgotSent(false); setErr(""); setPassword(""); }}
              className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-amber-200 mt-3 transition"
            >
              ← Back to Sign In
            </button>
          </div>
        )}

        <form onSubmit={submit} className={`space-y-3 ${mode === "forgot" && forgotSent ? "hidden" : ""}`}>
          {mode === "signup" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
                Name
              </label>
              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  data-testid="auth-name"
                  className="input-omni pl-9"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={mode === "signup"}
                />
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
              Email
            </label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                data-testid="auth-email"
                id="auth-signin-email"
                name="signin_email"
                type="email"
                className="input-omni pl-9"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username webauthn"
                required
              />
            </div>
          </div>
          {mode !== "forgot" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
                Password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  data-testid="auth-password"
                  type="password"
                  className="input-omni pl-9"
                  placeholder={mode === "signup" ? "Min 6 characters" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
              {/* 🔑 Forgot-password link — only visible in sign-in mode. */}
              {mode === "signin" && (
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setErr(""); setForgotSent(false); }}
                  data-testid="auth-forgot-link"
                  className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-amber-300 mt-1.5 ml-1 transition"
                >
                  Forgot password?
                </button>
              )}
            </div>
          )}

          {/* Remember me — keeps users signed in for 20 days via a refresh
              token. Default ON for convenience; users on shared computers can
              untick. Owner accounts are always trusted (server enforces). */}
          {mode !== "forgot" && (
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs uppercase tracking-widest text-neutral-400 hover:text-white">
              <input
                data-testid="auth-remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="accent-[#D4AF37] cursor-pointer"
              />
              <span>Keep me signed in <span className="text-neutral-600 normal-case tracking-normal">· 20 days</span></span>
            </label>
          )}

          {err && (
            <div data-testid="auth-error" className="text-sm text-[#EF4444] border border-[#DC2626] bg-[#DC2626]/10 px-3 py-2">
              {err}
            </div>
          )}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={busy}
            className="btn-omni btn-red w-full disabled:opacity-50"
          >
            {busy ? (
              <><Loader2 size={16} className="animate-spin" /> Working…</>
            ) : (
              <><Sparkles size={14} /> {mode === "signup" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Sign In"}</>
            )}
          </button>
        </form>

        {mode === "forgot" ? (
          !forgotSent && (
            <button
              data-testid="auth-back-to-signin"
              onClick={() => { setMode("signin"); setErr(""); setForgotSent(false); }}
              className="text-xs text-neutral-400 hover:text-[#D4AF37] mt-4 w-full text-center"
            >
              ← Back to Sign In
            </button>
          )
        ) : (
          <button
            data-testid="auth-toggle-mode"
            onClick={() => {
              setMode(mode === "signup" ? "signin" : "signup");
              setErr("");
            }}
            className="text-xs text-neutral-400 hover:text-[#D4AF37] mt-4 w-full text-center"
          >
            {mode === "signup"
              ? "Already have an account? Sign in"
              : "New to OmniMuscle? Create an account"}
          </button>
        )}
      </div>
    </div>
  );
}
